import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { authHeaders } from '../CuentaCompensacion'
import DeclaracionFormModal from './DeclaracionFormModal'
import DIMDataViewer from './DIMDataViewer'
import styles from './DeclaracionesPanel.module.css'

const API = import.meta.env.VITE_API_GATEWAY_URL

function formatFecha(ts) {
    if (!ts) return '—'
    const d = new Date(parseInt(ts) * 1000)
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatNum(v) {
    if (!v && v !== 0) return '—'
    return Number(v).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

function DeclaracionesPanel({ empresaId, op, canManage, onClose, setMsg }) {
    const [declaraciones, setDeclaraciones] = useState([])
    const [loading,  setLoading]  = useState(true)
    const [showForm, setShowForm] = useState(false)
    const [editing,  setEditing]  = useState(null)
    const [dimOpen,  setDimOpen]  = useState(null)  // declaración activa para DIM viewer

    const loadDeclaraciones = useCallback(async () => {
        setLoading(true)
        try {
            const headers = await authHeaders()
            const res = await axios.get(
                `${API}/cuenta-compensacion/${empresaId}/ops/${op.op_id}/declaraciones`,
                { headers }
            )
            setDeclaraciones(res.data.declaraciones || [])
        } catch (err) {
            setMsg({ type: 'error', text: 'Error cargando declaraciones: ' + (err.response?.data?.error || err.message) })
        } finally {
            setLoading(false)
        }
    }, [empresaId, op.op_id])  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadDeclaraciones() }, [loadDeclaraciones])

    const handleDelete = async (decl) => {
        if (!window.confirm(`¿Eliminar la declaración "${decl.numero_declaracion || decl.decl_id}"?`)) return
        try {
            const headers = await authHeaders()
            await axios.delete(
                `${API}/cuenta-compensacion/${empresaId}/ops/${op.op_id}/declaraciones/${decl.decl_id}`,
                { headers }
            )
            setMsg({ type: 'success', text: 'Declaración eliminada' })
            if (dimOpen?.decl_id === decl.decl_id) setDimOpen(null)
            loadDeclaraciones()
        } catch (err) {
            setMsg({ type: 'error', text: 'Error eliminando: ' + (err.response?.data?.error || err.message) })
        }
    }

    const handleSaved = () => {
        setShowForm(false)
        setEditing(null)
        setMsg({ type: 'success', text: editing ? 'Declaración actualizada' : 'Declaración agregada' })
        loadDeclaraciones()
    }

    const handleDIMSaved = async (decl, dimDatos) => {
        try {
            const headers = await authHeaders()
            await axios.put(
                `${API}/cuenta-compensacion/${empresaId}/ops/${op.op_id}/declaraciones/${decl.decl_id}`,
                { ...decl, dim_datos: dimDatos },
                { headers }
            )
            setMsg({ type: 'success', text: 'Datos DIM guardados en la declaración' })
            setDimOpen(null)
            loadDeclaraciones()
        } catch (err) {
            setMsg({ type: 'error', text: 'Error guardando datos DIM: ' + (err.response?.data?.error || err.message) })
        }
    }

    return (
        <div className={styles.panel}>
            {/* Header del panel */}
            <div className={styles.panelHeader}>
                <div className={styles.panelTitle}>
                    <button className={styles.btnBack} onClick={onClose} title="Cerrar panel">←</button>
                    <div>
                        <h2 className={styles.opTitle}>{op.op_numero}</h2>
                        <p className={styles.opSub}>{op.proveedor} · {op.moneda} {formatNum(op.valor_factura)}</p>
                    </div>
                </div>
                {canManage && (
                    <button className={styles.btnAdd} onClick={() => { setEditing(null); setShowForm(true) }}>
                        + Declaración de Importación
                    </button>
                )}
            </div>

            {/* Info de la OP */}
            <div className={styles.opInfo}>
                <div className={styles.infoItem}><span>OP</span><strong>{op.op_numero}</strong></div>
                <div className={styles.infoItem}><span>Factura</span><strong>{op.numero_factura || '—'}</strong></div>
                <div className={styles.infoItem}><span>Proveedor</span><strong>{op.proveedor || '—'}</strong></div>
                <div className={styles.infoItem}><span>Valor</span><strong>{op.moneda} {formatNum(op.valor_factura)}</strong></div>
                {op.factura_nombre && (
                    <div className={styles.infoItem}><span>Adjunto</span><strong>📄 {op.factura_nombre}</strong></div>
                )}
            </div>

            {/* Declaraciones */}
            <div className={styles.sectionTitle}>
                Declaraciones de Importación
                <span className={styles.count}>{declaraciones.length}</span>
            </div>

            {loading ? (
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <span>Cargando declaraciones...</span>
                </div>
            ) : declaraciones.length === 0 ? (
                <div className={styles.empty}>
                    <span>📦</span>
                    <p>No hay declaraciones de importación para esta OP.</p>
                    {canManage && (
                        <button className={styles.btnAddEmpty} onClick={() => { setEditing(null); setShowForm(true) }}>
                            + Agregar primera declaración
                        </button>
                    )}
                </div>
            ) : (
                <div className={styles.declList}>
                    {declaraciones.map((decl, idx) => (
                        <div key={decl.decl_id} className={`${styles.declCard} ${dimOpen?.decl_id === decl.decl_id ? styles.declCardActive : ''}`}>
                            <div className={styles.declHeader}>
                                <span className={styles.declNum}>
                                    DIM #{idx + 1}
                                    {decl.numero_declaracion && <em> — {decl.numero_declaracion}</em>}
                                </span>
                                <span className={styles.declFecha}>{decl.fecha_declaracion || formatFecha(decl.created_at)}</span>
                            </div>

                            <div className={styles.declMeta}>
                                {decl.valor_aduanas > 0 && <span>🏛 Aduana: {formatNum(decl.valor_aduanas)}</span>}
                                {decl.arancel > 0      && <span>📋 Arancel: {formatNum(decl.arancel)}</span>}
                                {decl.iva > 0          && <span>🧾 IVA: {formatNum(decl.iva)}</span>}
                                {decl.documento_nombre && <span>📎 {decl.documento_nombre}</span>}
                            </div>

                            {/* Resumen datos DIM extraídos */}
                            {decl.dim_datos && Object.keys(decl.dim_datos).length > 0 && (
                                <div className={styles.dimBadge}>✅ Datos DIM extraídos</div>
                            )}

                            <div className={styles.declActions}>
                                {decl.documento_s3_key && (
                                    <button
                                        className={styles.btnDIM}
                                        onClick={() => setDimOpen(dimOpen?.decl_id === decl.decl_id ? null : decl)}
                                    >
                                        {dimOpen?.decl_id === decl.decl_id ? 'Cerrar DIM' : '🔍 Extraer DIM'}
                                    </button>
                                )}
                                {canManage && (
                                    <>
                                        <button className={styles.btnEdit} onClick={() => { setEditing(decl); setShowForm(true) }}>✏</button>
                                        <button className={styles.btnDel} onClick={() => handleDelete(decl)}>🗑</button>
                                    </>
                                )}
                            </div>

                            {/* DIM Viewer inline */}
                            {dimOpen?.decl_id === decl.decl_id && (
                                <DIMDataViewer
                                    empresaId={empresaId}
                                    decl={decl}
                                    onSave={(datos) => handleDIMSaved(decl, datos)}
                                    onClose={() => setDimOpen(null)}
                                    setMsg={setMsg}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Modal formulario declaración */}
            {showForm && (
                <DeclaracionFormModal
                    empresaId={empresaId}
                    opId={op.op_id}
                    decl={editing}
                    onSaved={handleSaved}
                    onClose={() => { setShowForm(false); setEditing(null) }}
                    setMsg={setMsg}
                />
            )}
        </div>
    )
}

export default DeclaracionesPanel
